import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { GraduationCapIcon, ShieldCheckIcon, MailIcon, ArrowRightIcon, CheckIcon, LockIcon, ZapIcon, ClipboardListIcon, SparklesIcon, SchoolIcon, StarIcon, AwardIcon } from 'lucide-react';
import { getBridgeTemplate } from '../data/bridgeTemplates';
import { useMetaTags } from '../hooks/useMetaTags';
import { degreeLevelOptions, areaStudyOptions, programOptions, getDefaultProgram } from '../data/filterOptions';
const NAVY = '#1a2332';
const NAVY_ACCENT = '#2a374a';
const ORANGE = '#e8594f';
const ORANGE_HOVER = '#d64a41';
export function FindDegreesBridgePage() {
  const {
    templateId
  } = useParams<{
    templateId: string;
  }>();
  const template = getBridgeTemplate(templateId);
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // v6 selector state
  const [degreeLevel, setDegreeLevel] = useState<number | ''>('');
  const [areaOfStudy, setAreaOfStudy] = useState<number | ''>('');
  const [program, setProgram] = useState<number | ''>('');
  useMetaTags({
    title: `${template.headlineHighlight} | FindOnlineDegrees`,
    description: template.subhead
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    const params = new URLSearchParams(location.search);
    params.set('email', email);
    navigate(`/?${params.toString()}`);
  };
  const handleCtaClick = () => {
    setSubmitting(true);
    const params = new URLSearchParams(location.search);
    // v2: open the Media Matchers API as a pop-under and keep the user on
    // the listings page in the foreground tab.
    if (templateId === 'v2') {
      const externalParams = new URLSearchParams({
        sub1: params.get('sub1') || '',
        sub2: params.get('sub2') || '',
        sub3: params.get('sub3') || '',
        mediaType: '2',
        redirect: '1',
        mPubGroupID: 'PCGP4731148320'
      });
      const externalUrl = `https://api.media-matchers.com/search?${externalParams.toString()}`;
      try {
        const popunder = window.open(externalUrl, '_blank');
        if (popunder) {
          popunder.blur();
          window.focus();
        }
      } catch {
        // Popup blocked or failed — proceed with main navigation regardless.
      }
      navigate(`/?${params.toString()}`);
      return;
    }
    // v5: navigate to the colleges page (preserve any sub params)
    if (templateId === 'v5') {
      navigate(`/colleges?${params.toString()}`);
      return;
    }
    // v3 only: redirect to findonlinedegrees.org request-information page
    if (templateId === 'v3') {
      const externalParams = new URLSearchParams({
        sub1: params.get('sub1') || '',
        sub2: params.get('sub2') || '',
        sub3: params.get('sub3') || '',
        mPubGroupID: 'PCGP4731148320'
      });
      window.location.href = `https://findonlinedegrees.org/request-information?${externalParams.toString()}`;
      return;
    }
    navigate(`/?${params.toString()}`);
  };
  const isCta = template.variant === 'cta';
  const isSelector = template.variant === 'selector';
  const availablePrograms = areaOfStudy ? programOptions[areaOfStudy as number] || [] : [];
  const handleSelectorSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!degreeLevel || !areaOfStudy || !program) return;
    setSubmitting(true);
    const params = new URLSearchParams(location.search);
    params.set('degreeInterest', String(degreeLevel));
    params.set('mProgram', String(program));
    // v7: redirect to external Media Matchers search API,
    // and also open the home page (with the same selections) as a pop-under.
    if (templateId === 'v7') {
      const externalParams = new URLSearchParams({
        degreeInterest: String(degreeLevel),
        mProgram: String(program),
        sub1: params.get('sub1') || '',
        sub2: params.get('sub2') || '',
        sub3: params.get('sub3') || '',
        redirect: '1',
        mPubGroupID: 'PCGP4731148320'
      });
      const externalUrl = `https://api.media-matchers.com/search?${externalParams.toString()}`;
      // Pop-under: open the API URL in a new tab and drop it behind,
      // then navigate the current window to the home/listings page so the
      // user stays on our site.
      try {
        const popunder = window.open(externalUrl, '_blank');
        if (popunder) {
          popunder.blur();
          window.focus();
        }
      } catch {
        // Popup blocked or failed — proceed with main navigation regardless.
      }
      navigate(`/?${params.toString()}`);
      return;
    }
    navigate(`/?${params.toString()}`);
  };
  return <div className="min-h-screen flex flex-col font-sans text-gray-900 bg-gray-100" data-id="element-1129">
      {/* Slim Header — matches site nav style */}
      <header className="w-full relative overflow-hidden" style={{
      backgroundColor: NAVY
    }} data-id="element-1130">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#2a374a] to-transparent opacity-50 pointer-events-none" data-id="element-1131"></div>
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between relative z-10" data-id="element-1132">
          <div className="flex items-center gap-2.5" data-id="element-1133">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{
            backgroundColor: ORANGE
          }} data-id="element-1134">
              <GraduationCapIcon className="w-5 h-5 text-white" data-id="element-1135" />
            </div>
            <div className="leading-tight" data-id="element-1136">
              <div className="font-bold text-white text-base" data-id="element-1137">
                FindOnlineDegrees
              </div>
              <div className="text-[10px] text-gray-400 tracking-widest" data-id="element-1138">
                FREE DEGREE MATCHING
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-gray-300 text-xs" data-id="element-1139">
            <LockIcon className="w-3.5 h-3.5 text-white" data-id="element-1140" />
            <span data-id="element-1141">SSL Secured</span>
          </div>
        </div>
      </header>

      {/* Main Content — Two-Column, vertically centered */}
      <section className="flex-grow flex items-center" data-id="element-1142">
        <div className="w-full max-w-7xl mx-auto px-4 py-10 md:py-16 lg:py-20" data-id="element-1143">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center" data-id="element-1144">
            {/* LEFT: Headline */}
            <div className="text-center lg:text-left" data-id="element-1145">
              {/* Eyebrow */}
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full mb-5 text-[11px] font-bold tracking-widest" style={{
              backgroundColor: 'rgba(232,89,79,0.1)',
              color: ORANGE
            }} data-id="element-1146">
                <span className="w-1.5 h-1.5 rounded-full" style={{
                backgroundColor: ORANGE
              }} data-id="element-1147"></span>
                {template.eyebrow}
              </div>

              {/* Headline */}
              <h1 className="font-bold leading-[1.1] mb-5 text-gray-900" style={{
              fontSize: 'clamp(2rem, 4.5vw, 3.25rem)'
            }} data-id="element-1148">
                {template.headlineLead}{' '}
                <span style={{
                color: ORANGE
              }} data-id="element-1149">
                  {template.headlineHighlight}
                </span>{' '}
                {template.headlineTrail}
              </h1>

              <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-8 max-w-xl mx-auto lg:mx-0" data-id="element-1150">
                {template.subhead}
              </p>

              {/* Stats strip — site theme */}
              <div className="rounded-xl p-5 md:p-6 grid grid-cols-3 gap-4 max-w-md mx-auto lg:mx-0 relative overflow-hidden" style={{
              backgroundColor: NAVY
            }} data-id="element-1151">
                <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#2a374a] to-transparent opacity-50 pointer-events-none" data-id="element-1152"></div>
                <div className="text-center relative z-10" data-id="element-1153">
                  <div className="text-2xl md:text-3xl font-bold" style={{
                  color: ORANGE
                }} data-id="element-1154">
                    200+
                  </div>
                  <div className="text-[11px] text-gray-300 mt-1" data-id="element-1155">
                    Accredited Schools
                  </div>
                </div>
                <div className="text-center border-x border-white/10 relative z-10" data-id="element-1156">
                  <div className="text-2xl md:text-3xl font-bold" style={{
                  color: ORANGE
                }} data-id="element-1157">
                    1,400+
                  </div>
                  <div className="text-[11px] text-gray-300 mt-1" data-id="element-1158">
                    Online Programs
                  </div>
                </div>
                <div className="text-center relative z-10" data-id="element-1159">
                  <div className="text-2xl md:text-3xl font-bold" style={{
                  color: ORANGE
                }} data-id="element-1160">
                    100%
                  </div>
                  <div className="text-[11px] text-gray-300 mt-1" data-id="element-1161">
                    Free Matching
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT: Form — better aligned, cleaner spacing */}
            <aside className="w-full max-w-md mx-auto lg:max-w-none lg:mx-0" data-id="element-1162">
              {/* Step indicator */}
              <div className="flex items-center gap-3 mb-5 px-1" data-id="element-1163">
                <div className="flex items-center gap-2" data-id="element-1164">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-white" style={{
                  backgroundColor: ORANGE
                }} data-id="element-1165">
                    <CheckIcon className="w-4 h-4" data-id="element-1166" />
                  </div>
                  <span className="text-xs font-semibold text-gray-700" data-id="element-1167">
                    Profile Complete
                  </span>
                </div>
                <div className="flex-grow h-px bg-gray-300" data-id="element-1168"></div>
                <div className="flex items-center gap-2" data-id="element-1169">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold animate-pulse" style={{
                  backgroundColor: NAVY,
                  boxShadow: `0 0 0 4px rgba(232,89,79,0.15)`
                }} data-id="element-1170">
                    2
                  </div>
                  <span className="text-xs font-semibold" style={{
                  color: NAVY
                }} data-id="element-1171">
                    Confirm Email
                  </span>
                </div>
              </div>

              {isSelector ? <form onSubmit={handleSelectorSubmit} className="bg-white rounded-2xl p-7 md:p-9 shadow-xl border border-gray-200" data-id="element-1172">
                  <div className="text-center mb-6" data-id="element-1173">
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-1" data-id="element-1174">
                      {template.cardTitle}
                    </h2>
                    <p className="text-sm text-gray-600" data-id="element-1175">
                      {template.cardSubtitle}
                    </p>
                  </div>

                  <div className="space-y-5" data-id="element-1176">
                    <div data-id="element-1177">
                      <label className="block text-sm font-bold mb-2 text-gray-900" data-id="element-1178">
                        Degree Level <span className="text-red-500" data-id="element-1179">*</span>
                      </label>
                      <select value={degreeLevel} onChange={e => setDegreeLevel(e.target.value ? Number(e.target.value) : '')} required className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 bg-white text-gray-900 focus:outline-none focus:border-[#e8594f] focus:ring-2 focus:ring-[#e8594f]/20 hover:border-gray-300 transition-colors appearance-none cursor-pointer" style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.75rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.25em 1.25em',
                    paddingRight: '2.5rem'
                  }} data-id="element-1180">
                        <option value="" data-id="element-1181">-- Please Select --</option>
                        {degreeLevelOptions.filter(o => o.value !== 5).map(option => <option key={option.value} value={option.value} data-id="element-1182">
                              {option.label}
                            </option>)}
                      </select>
                    </div>

                    <div data-id="element-1183">
                      <label className="block text-sm font-bold mb-2 text-gray-900" data-id="element-1184">
                        Area of Study <span className="text-red-500" data-id="element-1185">*</span>
                      </label>
                      <select value={areaOfStudy} onChange={e => {
                    const v = e.target.value ? Number(e.target.value) : '';
                    setAreaOfStudy(v);
                    setProgram('');
                  }} required className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 bg-white text-gray-900 focus:outline-none focus:border-[#e8594f] focus:ring-2 focus:ring-[#e8594f]/20 hover:border-gray-300 transition-colors appearance-none cursor-pointer" style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.75rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.25em 1.25em',
                    paddingRight: '2.5rem'
                  }} data-id="element-1186">
                        <option value="" data-id="element-1187">-- Please Select --</option>
                        {areaStudyOptions.map(option => <option key={option.value} value={option.value} data-id="element-1188">
                            {option.label}
                          </option>)}
                      </select>
                    </div>

                    <div data-id="element-1189">
                      <label className="block text-sm font-bold mb-2 text-gray-900" data-id="element-1190">
                        Program <span className="text-red-500" data-id="element-1191">*</span>
                      </label>
                      <select value={program} onChange={e => setProgram(e.target.value ? Number(e.target.value) : '')} required disabled={!areaOfStudy} className="w-full px-4 py-3 rounded-lg border-2 border-gray-200 bg-white text-gray-900 focus:outline-none focus:border-[#e8594f] focus:ring-2 focus:ring-[#e8594f]/20 hover:border-gray-300 transition-colors appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed" style={{
                    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                    backgroundPosition: 'right 0.75rem center',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: '1.25em 1.25em',
                    paddingRight: '2.5rem'
                  }} data-id="element-1192">
                        <option value="" data-id="element-1193">
                          {areaOfStudy ? '-- Please Select --' : 'Select an area of study first'}
                        </option>
                        {availablePrograms.map(option => <option key={option.value} value={option.value} data-id="element-1194">
                            {option.label}
                          </option>)}
                      </select>
                    </div>

                    <button type="submit" disabled={submitting || !degreeLevel || !areaOfStudy || !program} className="w-full flex items-center justify-center gap-2 text-white font-bold py-4 px-6 rounded-lg shadow-lg transition-all hover:shadow-xl active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed" style={{
                  backgroundColor: ORANGE
                }} onMouseEnter={e => {
                  if (!(e.currentTarget as HTMLButtonElement).disabled) {
                    ;
                    (e.currentTarget as HTMLButtonElement).style.backgroundColor = ORANGE_HOVER;
                  }
                }} onMouseLeave={e => {
                  ;
                  (e.currentTarget as HTMLButtonElement).style.backgroundColor = ORANGE;
                }} data-id="element-1195">
                      <span data-id="element-1196">{submitting ? 'Loading…' : template.ctaLabel}</span>
                      <ArrowRightIcon className="w-4 h-4" data-id="element-1197" />
                    </button>

                    <p className="text-center text-xs text-gray-500 flex items-center justify-center gap-1.5" data-id="element-1198">
                      <LockIcon className="w-3.5 h-3.5" data-id="element-1199" />
                      Your information is secure and confidential
                    </p>
                  </div>
                </form> : isCta ? <div className="bg-white rounded-2xl p-8 md:p-10 shadow-xl border border-gray-200 text-center" data-id="element-1200">
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" data-id="element-1201">
                    {template.cardHeadline || template.cardTitle}
                  </h2>
                  <p className="text-base font-semibold mb-7" style={{
                color: ORANGE
              }} data-id="element-1202">
                    {template.cardSubcopy || template.cardSubtitle}
                  </p>

                  <button type="button" onClick={handleCtaClick} disabled={submitting} className="inline-flex items-center justify-center gap-2 text-white font-bold py-4 px-10 rounded-lg shadow-lg transition-all hover:shadow-xl active:scale-[0.99] disabled:opacity-70" style={{
                backgroundColor: ORANGE
              }} onMouseEnter={e => {
                ;
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = ORANGE_HOVER;
              }} onMouseLeave={e => {
                ;
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = ORANGE;
              }} data-id="element-1203">
                    <span data-id="element-1204">{submitting ? 'Loading…' : template.ctaLabel}</span>
                    <ArrowRightIcon className="w-4 h-4" data-id="element-1205" />
                  </button>

                  {template.cardFootnote && <p className="text-sm text-gray-600 mt-7 leading-relaxed max-w-sm mx-auto" data-id="element-1206">
                      {template.cardFootnote}
                    </p>}
                </div> : <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-7 md:p-9 shadow-xl border border-gray-200" data-id="element-1207">
                  <label htmlFor="bridge-email" className="block text-sm font-bold mb-3 text-gray-900" data-id="element-1208">
                    Where should we send your matches?
                  </label>

                  <div className="relative mb-4" data-id="element-1209">
                    <MailIcon className="w-5 h-5 text-gray-400 absolute left-4 top-1/2 -translate-y-1/2" data-id="element-1210" />
                    <input id="bridge-email" type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="yourname@example.com" className="w-full pl-12 pr-4 py-3.5 rounded-lg border-2 border-gray-200 bg-white text-base text-gray-900 focus:outline-none focus:border-[#e8594f] focus:ring-2 focus:ring-[#e8594f]/20 hover:border-gray-300 transition-colors" data-id="element-1211" />
                  </div>

                  <button type="submit" disabled={submitting} className="w-full flex items-center justify-center gap-2 text-white font-bold py-4 px-6 rounded-lg shadow-lg transition-all hover:shadow-xl active:scale-[0.99] disabled:opacity-70" style={{
                backgroundColor: ORANGE
              }} onMouseEnter={e => {
                ;
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = ORANGE_HOVER;
              }} onMouseLeave={e => {
                ;
                (e.currentTarget as HTMLButtonElement).style.backgroundColor = ORANGE;
              }} data-id="element-1212">
                    <span data-id="element-1213">{submitting ? 'Loading…' : template.ctaLabel}</span>
                    <ArrowRightIcon className="w-4 h-4" data-id="element-1214" />
                  </button>

                  {/* Inline trust row */}
                  <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-5 text-xs text-gray-600" data-id="element-1215">
                    <div className="flex items-center gap-1.5" data-id="element-1216">
                      <ShieldCheckIcon className="w-3.5 h-3.5 text-green-500" data-id="element-1217" />
                      <span data-id="element-1218">No credit card</span>
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-1219">
                      <ZapIcon className="w-3.5 h-3.5 text-green-500" data-id="element-1220" />
                      <span data-id="element-1221">Instant results</span>
                    </div>
                    <div className="flex items-center gap-1.5" data-id="element-1222">
                      <CheckIcon className="w-3.5 h-3.5 text-green-500" data-id="element-1223" />
                      <span data-id="element-1224">100% free</span>
                    </div>
                  </div>

                  <p className="text-[11px] text-gray-500 mt-5 leading-relaxed text-center" data-id="element-1225">
                    By clicking, you agree to our{' '}
                    <a href="/terms" className="underline text-gray-700 hover:text-gray-900" data-id="element-1226">
                      Terms
                    </a>{' '}
                    &{' '}
                    <a href="/privacy-policy" className="underline text-gray-700 hover:text-gray-900" data-id="element-1227">
                      Privacy Policy
                    </a>{' '}
                    and consent to receive program info via email, phone, or
                    text.
                  </p>
                </form>}
            </aside>
          </div>
        </div>
      </section>

      {/* Content Section — How it works + Testimonials + Trust */}
      <section className="w-full bg-white border-t border-gray-200" data-id="element-1228">
        <div className="max-w-7xl mx-auto px-4 py-16 md:py-20" data-id="element-1229">
          {/* How It Works */}
          <div className="text-center mb-12" data-id="element-1230">
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full mb-4 text-[11px] font-bold tracking-widest" style={{
            backgroundColor: 'rgba(232,89,79,0.1)',
            color: ORANGE
          }} data-id="element-1231">
              <span className="w-1.5 h-1.5 rounded-full" style={{
              backgroundColor: ORANGE
            }} data-id="element-1232"></span>
              HOW IT WORKS
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" data-id="element-1233">
              Your Path to the Right Program in 3 Steps
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-1234">
              We've helped thousands of students find accredited online programs
              that fit their goals, schedule, and budget.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-16" data-id="element-1235">
            {[{
            icon: ClipboardListIcon,
            step: '01',
            title: 'Tell Us About You',
            body: 'Share your goals, education level, and area of interest in under a minute.'
          }, {
            icon: SparklesIcon,
            step: '02',
            title: 'Get Matched Instantly',
            body: 'Our system pairs you with accredited online programs that fit your profile.'
          }, {
            icon: SchoolIcon,
            step: '03',
            title: 'Connect With Schools',
            body: 'Compare programs side-by-side and connect directly with admissions teams.'
          }].map((s, i) => {
            const Icon = s.icon;
            return <div key={i} className="relative bg-gray-50 rounded-2xl p-7 border border-gray-200 hover:border-[#e8594f]/40 hover:shadow-md transition-all" data-id="element-1236">
                  <div className="flex items-center justify-between mb-5" data-id="element-1237">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{
                  backgroundColor: 'rgba(232,89,79,0.1)'
                }} data-id="element-1238">
                      <Icon className="w-6 h-6" style={{
                    color: ORANGE
                  }} data-id="element-1239" />
                    </div>
                    <span className="text-3xl font-bold text-gray-200" data-id="element-1240">
                      {s.step}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1241">
                    {s.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1242">
                    {s.body}
                  </p>
                </div>;
          })}
          </div>

          {/* Testimonials */}
          {template.testimonials && template.testimonials.length > 0 && <div className="border-t border-gray-200 pt-16" data-id="element-1243">
              <div className="text-center mb-10" data-id="element-1244">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3" data-id="element-1245">
                  Trusted by Students Like You
                </h2>
                <div className="flex items-center justify-center gap-1.5 text-gray-600 text-sm" data-id="element-1246">
                  {[1, 2, 3, 4, 5].map(i => <StarIcon key={i} className="w-4 h-4 fill-current" style={{
                color: ORANGE
              }} data-id="element-1247" />)}
                  <span className="ml-2" data-id="element-1248">
                    4.8 average rating from 70,000+ matched students
                  </span>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-1249">
                {template.testimonials.map((t, i) => <div key={i} className="bg-gray-50 rounded-2xl p-6 md:p-7 border border-gray-200" data-id="element-1250">
                    <div className="flex gap-1 mb-3" data-id="element-1251">
                      {[1, 2, 3, 4, 5].map(s => <StarIcon key={s} className="w-4 h-4 fill-current" style={{
                  color: ORANGE
                }} data-id="element-1252" />)}
                    </div>
                    <p className="text-gray-700 leading-relaxed mb-5 italic" data-id="element-1253">
                      "{t.quote}"
                    </p>
                    <div className="flex items-center gap-3 pt-4 border-t border-gray-200" data-id="element-1254">
                      <div className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold" style={{
                  backgroundColor: NAVY
                }} data-id="element-1255">
                        {t.initial}
                      </div>
                      <div data-id="element-1256">
                        <div className="font-semibold text-gray-900 text-sm" data-id="element-1257">
                          {t.name}
                        </div>
                        <div className="text-xs text-gray-500" data-id="element-1258">{t.school}</div>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>}

          {/* Trust band */}
          <div className="mt-16 rounded-2xl p-8 md:p-10 relative overflow-hidden" style={{
          backgroundColor: NAVY
        }} data-id="element-1259">
            <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#2a374a] to-transparent opacity-50 pointer-events-none" data-id="element-1260"></div>
            <div className="relative z-10 grid md:grid-cols-3 gap-6 items-center" data-id="element-1261">
              <div className="flex items-start gap-3" data-id="element-1262">
                <AwardIcon className="w-7 h-7 flex-shrink-0 mt-0.5" style={{
                color: ORANGE
              }} data-id="element-1263" />
                <div data-id="element-1264">
                  <div className="font-bold text-white mb-1" data-id="element-1265">
                    Only Accredited Schools
                  </div>
                  <div className="text-xs text-gray-300 leading-relaxed" data-id="element-1266">
                    Every partner school is regionally or nationally accredited.
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-1267">
                <ShieldCheckIcon className="w-7 h-7 flex-shrink-0 mt-0.5" style={{
                color: ORANGE
              }} data-id="element-1268" />
                <div data-id="element-1269">
                  <div className="font-bold text-white mb-1" data-id="element-1270">
                    Your Privacy Protected
                  </div>
                  <div className="text-xs text-gray-300 leading-relaxed" data-id="element-1271">
                    SSL encrypted. We never sell your data to unrelated third
                    parties.
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-1272">
                <ZapIcon className="w-7 h-7 flex-shrink-0 mt-0.5" style={{
                color: ORANGE
              }} data-id="element-1273" />
                <div data-id="element-1274">
                  <div className="font-bold text-white mb-1" data-id="element-1275">
                    Always 100% Free
                  </div>
                  <div className="text-xs text-gray-300 leading-relaxed" data-id="element-1276">
                    No credit card, no fees — for you, ever.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="w-full border-t border-gray-200 bg-white" data-id="element-1277">
        <div className="max-w-7xl mx-auto px-4 py-8 text-center text-gray-700" data-id="element-1278">
          <nav className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs mb-4" data-id="element-1279">
            <a href="/privacy-policy" className="hover:text-[#e8594f] transition-colors" data-id="element-1280">
              Privacy Policy
            </a>
            <a href="/terms" className="hover:text-[#e8594f] transition-colors" data-id="element-1281">
              Terms of Use
            </a>
            <a href="/ca-notice" className="hover:text-[#e8594f] transition-colors" data-id="element-1282">
              CA Privacy Rights
            </a>
            <a href="/cookie-policy" className="hover:text-[#e8594f] transition-colors" data-id="element-1283">
              Cookies
            </a>
          </nav>
          <p className="text-xs text-gray-600 mb-1" data-id="element-1284">
            © {new Date().getFullYear()} FindOnlineDegrees.org · Media Matchers
            LLC · All Rights Reserved.
          </p>
          <p className="text-[11px] text-gray-500 max-w-2xl mx-auto leading-relaxed" data-id="element-1285">
            FindOnlineDegrees.org is a free service that helps students explore
            accredited online degree programs. We may receive compensation from
            school partners when users inquire.
          </p>
        </div>
      </footer>
    </div>;
}